import { isDecimalAmount, multiplyAmount, sumAmounts } from '@platform/design-system';
import { z } from 'zod';
import { nameIn } from '../catalog/categories.ts';
import { variantLabel, type OptionIndex } from '../catalog/options.ts';
import type { Product } from '../catalog/product.ts';
import { companyOf, MAIN_COMPANY, subsidiaryIdOf } from '../companies.ts';
import { ORDER_SOURCES, type AmendOrderBody, type CreateOrderBody, type Order } from './orders.ts';

export const NO_SOURCE = '';

export const NO_SHIPPING_FEE = '0';

export const ORDER_FORM_LIMITS = { address: 500, postalCode: 20, note: 1000, quantity: 10_000 } as const;

const amountSchema = z.string().trim().refine(isDecimalAmount);

export const orderLineSchema = z.object({
  variantId: z.string(),
  label: z.string(),
  sku: z.string(),
  quantity: z.number().int().min(1).max(ORDER_FORM_LIMITS.quantity),
  unitPrice: amountSchema,
});

export const newOrderFormSchema = z.object({
  address: z.string().trim().min(1).max(ORDER_FORM_LIMITS.address),
  governorate: z.string().min(1),
  delegation: z.string(),
  postalCode: z.string().max(ORDER_FORM_LIMITS.postalCode),
  locationId: z.string().min(1),
  company: z.string().min(1),
  isExchange: z.boolean(),
  source: z.enum([NO_SOURCE, ...ORDER_SOURCES]),
  note: z.string().max(ORDER_FORM_LIMITS.note),
  shippingFee: amountSchema,
  lines: z.array(orderLineSchema).min(1),
});

export type NewOrderFormValues = z.infer<typeof newOrderFormSchema>;
export type OrderLineValues = z.infer<typeof orderLineSchema>;

export const emptyOrderForm = (locationId: string): NewOrderFormValues => ({
  address: '',
  governorate: '',
  delegation: '',
  postalCode: '',
  locationId,
  company: MAIN_COMPANY,
  isExchange: false,
  source: NO_SOURCE,
  note: '',
  shippingFee: NO_SHIPPING_FEE,
  lines: [],
});

const orNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());

const editableBodyOf = (values: NewOrderFormValues) => ({
  shipping: {
    address: values.address.trim(),
    governorate: values.governorate,
    delegation: orNull(values.delegation),
    postalCode: orNull(values.postalCode),
  },
  subsidiaryId: subsidiaryIdOf(values.company),
  isExchange: values.isExchange,
  source: values.source === NO_SOURCE ? null : values.source,
  note: orNull(values.note),
  shippingFee: values.shippingFee.trim(),
  lines: values.lines.map((line) => ({
    variantId: line.variantId,
    quantity: line.quantity,
    unitPrice: line.unitPrice.trim(),
  })),
});

export const orderBodyOf = (customerId: string, values: NewOrderFormValues): CreateOrderBody => ({
  customerId,
  locationId: values.locationId,
  ...editableBodyOf(values),
});

export const amendBodyOf = (version: number, values: NewOrderFormValues): AmendOrderBody => ({
  version,
  ...editableBodyOf(values),
});

export const orderFormValuesOf = (order: Order): NewOrderFormValues => ({
  address: order.shipping.address ?? '',
  governorate: order.shipping.governorate,
  delegation: order.shipping.delegation ?? '',
  postalCode: order.shipping.postalCode ?? '',
  locationId: order.locationId,
  company: companyOf(order.subsidiaryId),
  isExchange: order.isExchange,
  source: order.source ?? NO_SOURCE,
  note: order.note ?? '',
  shippingFee: order.totals.shippingFee,
  lines: order.lines.map((line) => ({
    variantId: line.variantId,
    label: line.productName,
    sku: line.sku,
    quantity: line.quantity,
    unitPrice: line.unitPrice,
  })),
});

const validAmount = (amount: string): string => (isDecimalAmount(amount.trim()) ? amount.trim() : '0');

const validQuantity = (quantity: number): number =>
  Number.isInteger(quantity) && quantity > 0 ? quantity : 0;

export const lineTotalOf = (line: Pick<OrderLineValues, 'quantity' | 'unitPrice'>): string =>
  multiplyAmount(validAmount(line.unitPrice), validQuantity(line.quantity));

export type OrderPreview = { readonly itemsSubtotal: string; readonly total: string };

export const previewOf = (lines: readonly OrderLineValues[], shippingFee: string): OrderPreview => {
  const itemsSubtotal = sumAmounts(lines.map(lineTotalOf));
  return { itemsSubtotal, total: sumAmounts([itemsSubtotal, validAmount(shippingFee)]) };
};

export type OrderReadiness = {
  readonly items: boolean;
  readonly customer: boolean;
  readonly delivery: boolean;
};

export type ReadinessInput = Pick<NewOrderFormValues, 'lines' | 'address' | 'governorate'> & {
  readonly customerChosen: boolean;
};

export const readinessOf = ({
  lines,
  address,
  governorate,
  customerChosen,
}: ReadinessInput): OrderReadiness => ({
  items: lines.length > 0,
  customer: customerChosen,
  delivery: address.trim() !== '' && governorate !== '',
});

export const unitsOf = (lines: readonly OrderLineValues[]): number =>
  lines.reduce((units, line) => units + validQuantity(line.quantity), 0);

export type SaleableVariant = {
  readonly variantId: string;
  readonly label: string;
  readonly sku: string;
  readonly price: string;
};

export const saleableVariantsOf = (
  products: readonly Product[],
  index: OptionIndex,
  locale: string,
): readonly SaleableVariant[] =>
  products.flatMap((product) => {
    const name = nameIn(product, locale, product.reference ?? product.id);
    return product.variants.map((variant) => {
      const options = variantLabel(variant.optionValueIds, index, '');
      return {
        variantId: variant.id,
        label: options === '' ? name : `${name} · ${options}`,
        sku: variant.sku,
        price: variant.price.amount,
      };
    });
  });

export const lineOf = (variant: SaleableVariant): OrderLineValues => ({
  variantId: variant.variantId,
  label: variant.label,
  sku: variant.sku,
  quantity: 1,
  unitPrice: variant.price,
});
